import {
  DECLARED_PAGE_KEYS,
  type DeclaredPageKey,
} from '@hurdl/admin-panel-backend/product';
import { Link } from '@tanstack/react-router';
import { type ReactElement } from 'react';

import { BESPOKE_PAGES } from '../bespoke/registry.js';
import { LANDED, PRODUCTS, pageAvailability } from '../products.js';
import { DeclaredPage } from './declared-page.js';

/**
 * The page framework (R2.3, R2.4, R2.5): render the page a Product enables under a
 * key, as a Declared_Page or a Bespoke_Page.
 *
 * A Declared_Page renders availability-aware from the composed capabilities; a
 * Bespoke_Page renders the registered module for its one Product alone, and a
 * Declared_Page depends on none of them. A key a Product does not enable, or that no
 * registered Bespoke_Page answers, renders a not-found rather than a blank page.
 */
export function ProductPage({
  productKey,
  pageKey,
}: {
  readonly productKey: string;
  readonly pageKey: string;
}): ReactElement {
  const product = PRODUCTS.find((candidate) => candidate.key === productKey);

  if (product === undefined) {
    return <NotFound />;
  }

  // A Bespoke_Page the Product declares and the registry answers.
  const bespoke = product.bespokePages?.find((page) => page.key === pageKey);
  if (bespoke !== undefined) {
    const registered = BESPOKE_PAGES[`${productKey}:${pageKey}`];
    if (registered !== undefined) {
      return registered.component();
    }
  }

  // A Declared_Page the Product enables.
  if (isDeclaredPageKey(pageKey) && product.enabledPages.includes(pageKey)) {
    return (
      <DeclaredPage
        pageKey={pageKey}
        availability={pageAvailability(product, pageKey, LANDED)}
      />
    );
  }

  return <NotFound />;
}

/** Whether a string is one of the closed Declared_Page keys. */
function isDeclaredPageKey(value: string): value is DeclaredPageKey {
  return (DECLARED_PAGE_KEYS as ReadonlyArray<string>).includes(value);
}

function NotFound(): ReactElement {
  return (
    <div>
      <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
        Page not found
      </h1>
      <p>
        <Link to="/">Back to the panel</Link>
      </p>
    </div>
  );
}
